/**
 * Webhook-endpoints tab for the tenant detail page (spec 0008 §5.6).
 *
 * - Lists every webhook endpoint on the tenant via
 *   ``GET /admin-api/v1/webhook-endpoints/?tenant_id=…``.
 * - "+ Add endpoint" opens a Dialog: enter URL + pick events →
 *   ``POST /webhook-endpoints/`` → reveal the signing secret ONCE.
 * - "Edit" opens the same Dialog pre-filled → ``PATCH`` URL +
 *   enabled_events (no secret reveal).
 * - Per row: enable/disable, rotate-secret (reveals a fresh secret).
 *
 * The signing secret is the only sensitive value the operator copies;
 * it is returned exactly once on create / rotate and never on a read
 * (see ``WebhookSecretRevealModal``).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  asApiError,
  createWebhookEndpoint,
  getWebhookEndpoints,
  patchWebhookEndpoint,
  rotateWebhookEndpointSecret,
  WEBHOOK_EVENT_TYPES,
  type WebhookEndpointRow,
  type WebhookEndpointSecretResponse,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { WebhookSecretRevealModal } from "./WebhookSecretRevealModal";

const DEFAULT_EVENTS = ["intent.succeeded", "intent.failed"];

interface FormState {
  /** ``null`` = add new; otherwise edit this existing row. */
  editing: WebhookEndpointRow | null;
  url: string;
  events: string[];
}

const CLOSED_FORM: FormState = {
  editing: null,
  url: "",
  events: DEFAULT_EVENTS,
};

export function WebhookEndpointsTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(CLOSED_FORM);
  const [revealed, setRevealed] = useState<WebhookEndpointSecretResponse | null>(
    null,
  );
  const [rotateTarget, setRotateTarget] = useState<WebhookEndpointRow | null>(
    null,
  );

  const query = useQuery({
    queryKey: ["webhook-endpoints", { tenantId }] as const,
    queryFn: () => getWebhookEndpoints({ tenant_id: tenantId }),
  });

  // Reset the form when the dialog closes so a future "+ Add endpoint"
  // opens fresh (and never bleeds the previous edit's values).
  useEffect(() => {
    if (!formOpen) setForm(CLOSED_FORM);
  }, [formOpen]);

  function openAdd() {
    setForm(CLOSED_FORM);
    setFormOpen(true);
  }

  function openEdit(row: WebhookEndpointRow) {
    setForm({
      editing: row,
      url: row.url,
      // Defensive clone — the input may otherwise mutate the cached
      // react-query row via the event-toggle setState path.
      events: [...row.enabled_events],
    });
    setFormOpen(true);
  }

  const createMut = useMutation({
    mutationFn: () =>
      createWebhookEndpoint({
        tenant_id: tenantId,
        url: form.url.trim(),
        enabled_events: form.events,
      }),
    onSuccess: async (resp) => {
      setFormOpen(false);
      setRevealed(resp);
      await qc.invalidateQueries({ queryKey: ["webhook-endpoints", { tenantId }] });
    },
    onError: (err) => surfaceError(err, "Couldn't create endpoint"),
  });

  const editMut = useMutation({
    mutationFn: () => {
      if (!form.editing) throw new Error("edit mutation called without a target");
      return patchWebhookEndpoint(form.editing.id, {
        url: form.url.trim(),
        enabled_events: form.events,
      });
    },
    onSuccess: async () => {
      setFormOpen(false);
      toast.success("Endpoint updated");
      await qc.invalidateQueries({ queryKey: ["webhook-endpoints", { tenantId }] });
    },
    onError: (err) => surfaceError(err, "Couldn't update endpoint"),
  });

  const toggleMut = useMutation({
    mutationFn: (vars: { id: string; status: "active" | "disabled" }) =>
      patchWebhookEndpoint(vars.id, { status: vars.status }),
    onSuccess: async (_row, vars) => {
      toast.success(vars.status === "disabled" ? "Endpoint disabled" : "Endpoint enabled");
      await qc.invalidateQueries({ queryKey: ["webhook-endpoints", { tenantId }] });
    },
    onError: (err) => surfaceError(err, "Status change failed"),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotateWebhookEndpointSecret(id),
    onSuccess: async (resp) => {
      setRotateTarget(null);
      setRevealed(resp);
      await qc.invalidateQueries({ queryKey: ["webhook-endpoints", { tenantId }] });
    },
    onError: (err) => surfaceError(err, "Couldn't rotate secret"),
  });

  function toggleEvent(evt: string, checked: boolean) {
    setForm((prev) => ({
      ...prev,
      events: checked
        ? [...prev.events, evt]
        : prev.events.filter((e) => e !== evt),
    }));
  }

  const rows = query.data?.results ?? [];
  const total = query.data?.total ?? rows.length;
  const isEditing = form.editing !== null;
  const submitMut = isEditing ? editMut : createMut;
  const submitPending = submitMut.isPending;
  // Lightweight client-side guard so the operator sees the error before
  // the round-trip. The server still rejects http:// outside DEBUG —
  // this just shortens the feedback loop.
  const trimmedUrl = form.url.trim();
  const urlIsHttps = trimmedUrl.startsWith("https://");
  const canSubmit =
    trimmedUrl.length > 0 &&
    urlIsHttps &&
    form.events.length > 0 &&
    !submitPending;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Webhook endpoints</CardTitle>
            <CardDescription>
              Where Senti delivers signed payment-intent webhooks for this
              tenant. The signing secret is shown once at create / rotate.
            </CardDescription>
          </div>
          <Button onClick={openAdd} data-testid="webhook-add">
            + Add endpoint
          </Button>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No webhook endpoints yet. Add one to start receiving delivery
              callbacks.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>URL</TableHead>
                      <TableHead>Events</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Env</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead aria-label="Actions" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-mono text-xs break-all max-w-xs">
                          {row.url}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.enabled_events.join(", ")}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              row.status === "active"
                                ? "text-emerald-700 dark:text-emerald-300"
                                : "text-muted-foreground"
                            }
                          >
                            {row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs uppercase text-muted-foreground">
                          {row.tenant.environment}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(row.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(row)}
                              data-testid={`webhook-edit-${row.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRotateTarget(row)}
                              data-testid={`webhook-rotate-${row.id}`}
                            >
                              Rotate secret
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={toggleMut.isPending}
                              onClick={() =>
                                toggleMut.mutate({
                                  id: row.id,
                                  status: row.status === "active" ? "disabled" : "active",
                                })
                              }
                              data-testid={`webhook-toggle-${row.id}`}
                            >
                              {row.status === "active" ? "Disable" : "Enable"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/*
                We page through at most 100 rows from the list endpoint
                (see ``getWebhookEndpoints``). Surface the truncation
                explicitly so an operator on a tenant with >100 endpoints
                doesn't silently see a partial view.
              */}
              {total > rows.length ? (
                <p
                  className="mt-3 text-xs text-muted-foreground"
                  data-testid="webhook-truncation-notice"
                >
                  Showing {rows.length} of {total} endpoints. Use the API to
                  page beyond this view.
                </p>
              ) : (
                <p className="mt-3 text-xs text-muted-foreground">
                  Showing {rows.length} of {total}.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add / edit dialog — same form, different submit action. */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit webhook endpoint" : "Add webhook endpoint"}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Update the URL and events this endpoint receives. The existing signing secret is preserved — rotate it from the row's action if needed."
                : "Senti POSTs signed events here. The signing secret is returned exactly once after creation."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="webhook-url">Endpoint URL</Label>
              <Input
                id="webhook-url"
                type="url"
                pattern="https://.*"
                value={form.url}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, url: e.target.value }))
                }
                placeholder="https://console.example.com/api/v1/senti/webhooks/"
                disabled={submitPending}
                data-testid="webhook-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Must be an https:// URL that returns 2xx.
              </p>
              {trimmedUrl.length > 0 && !urlIsHttps ? (
                <p
                  className="text-xs text-destructive"
                  data-testid="webhook-url-error"
                >
                  URL must start with <code>https://</code>.
                </p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label>Events</Label>
              <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-y-auto">
                {WEBHOOK_EVENT_TYPES.map((evt) => (
                  <label key={evt} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={form.events.includes(evt)}
                      onChange={(e) => toggleEvent(evt, e.target.checked)}
                      disabled={submitPending}
                      data-testid={`webhook-event-${evt}`}
                    />
                    <code className="font-mono text-xs">{evt}</code>
                  </label>
                ))}
              </div>
              {form.events.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  Select at least one event.
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFormOpen(false)}
              disabled={submitPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                isEditing ? editMut.mutate() : createMut.mutate()
              }
              disabled={!canSubmit}
              data-testid={isEditing ? "webhook-edit-confirm" : "webhook-create-confirm"}
            >
              {submitPending
                ? isEditing
                  ? "Saving…"
                  : "Creating…"
                : isEditing
                  ? "Save changes"
                  : "Create endpoint"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate-secret confirm dialog */}
      <Dialog
        open={!!rotateTarget}
        onOpenChange={(open) => {
          if (!open) setRotateTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotate signing secret?</DialogTitle>
            <DialogDescription>
              A fresh secret is minted and shown once. The current secret stops
              working immediately — update your handler before rotating.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotateTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => rotateTarget && rotateMut.mutate(rotateTarget.id)}
              disabled={rotateMut.isPending}
              data-testid="webhook-rotate-confirm"
            >
              {rotateMut.isPending ? "Rotating…" : "Rotate secret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time secret reveal */}
      {revealed ? (
        <WebhookSecretRevealModal
          secret={revealed.secret}
          url={revealed.webhook_endpoint.url}
          onDone={() => setRevealed(null)}
        />
      ) : null}
    </>
  );
}

function surfaceError(err: unknown, title: string) {
  const body = asApiError(err);
  // The backend's ``duplicate_endpoint`` slug lands here when the
  // (tenant, url) uniqueness fires; show its ``detail`` (which carries
  // the conflicting endpoint id) directly so the operator can find it.
  toast.error(title, {
    description: body?.detail ?? body?.error ?? "Check your network and retry.",
  });
}

export default WebhookEndpointsTab;
